import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SortingOption } from '@sorare/core/src/__generated__/globalTypes';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { Accordion } from '@sorare/core/src/atoms/layout/Accordion';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { extractConnectionData } from '@sorare/core/src/gql/extractData';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useQueryState } from '@sorare/core/src/hooks/useQueryState';
import { filters } from '@sorare/core/src/lib/glossary';

import { MyPage } from '../MyPage';
import { DisplayItems } from '../common/DisplayItems';
import { PrimaryOffer } from '../common/PrimaryOffer';
import { messages } from '../common/messages';
import { DefaultSortType } from '../common/types';
import { useSportSelect } from '../common/useSportSelect';
import type {
  BoughtPrimaryOffersQuery,
  BoughtPrimaryOffersQueryVariables,
} from './__generated__/index.graphql';

const BOUGHT_PRIMARY_OFFERS_QUERY = gql`
  query BoughtPrimaryOffersQuery(
    $cursor: String
    $sortByEndDate: SortingOption
    $sport: [Sport!]
  ) {
    currentUser {
      slug
      boughtPrimaryOffers(
        first: 10
        after: $cursor
        sortByEndDate: $sortByEndDate
        sport: $sport
      ) {
        nodes {
          id
          ...MySorarePrimaryOffer_primaryOffer
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${PrimaryOffer.fragments.primaryOffer}
` as TypedDocumentNode<
  BoughtPrimaryOffersQuery,
  BoughtPrimaryOffersQueryVariables
>;

export const MyPrimaryOffers = () => {
  const { sportInputValue, sportSelectNode } = useSportSelect();
  const { formatMessage } = useIntl();

  const [{ sortType }, setState] = useQueryState({
    sortType: DefaultSortType.LATEST as DefaultSortType,
  });

  const sortOptions = useMemo(
    () =>
      Object.values(DefaultSortType).map((element: DefaultSortType) => ({
        label: formatMessage(messages[element]),
        value: element,
      })),
    [formatMessage]
  );

  const onSortTypeChange = (value: DefaultSortType) => {
    setState(prevState => ({ ...prevState, sortType: value }));
  };
  const sortByEndDate =
    sortType === DefaultSortType.LATEST
      ? SortingOption.DESC
      : SortingOption.ASC;
  const { loading, data, loadMore } = usePaginatedQuery(
    BOUGHT_PRIMARY_OFFERS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      connection: 'TokenPrimaryOfferConnection',
      variables: {
        sortByEndDate,
        sport: sportInputValue,
      },
    }
  );

  const { items, count, cursor, hasMore } = extractConnectionData(
    data?.currentUser?.boughtPrimaryOffers,
    primaryOffer => (
      <PrimaryOffer key={primaryOffer.id} primaryOffer={primaryOffer} />
    )
  );

  const displayLoading = !items && loading;

  return (
    <MyPage
      toolbar={
        <>
          {sportSelectNode}
          <Accordion
            startsOpen
            title={
              <LabelM bold>
                <FormattedMessage {...filters.sortBy} />
              </LabelM>
            }
          >
            <RadioGroup
              name="sort-type-selector"
              options={sortOptions}
              value={sortType}
              onChange={onSortTypeChange}
            />
          </Accordion>
        </>
      }
    >
      <DisplayItems
        displayLoading={displayLoading}
        count={count}
        items={items}
        loadMoreSection={{
          hasMore,
          loading,
          loadMore: () => {
            loadMore(false, {
              cursor,
              sortByEndDate,
              sport: sportInputValue,
            });
          },
        }}
      />
    </MyPage>
  );
};
